import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Plus, Pencil, Trash2, Upload, Eye, Image as ImageIcon, Calendar as CalendarIcon, Check, X, ExternalLink } from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminExhibitions = () => {
  const [exhibitions, setExhibitions] = useState([]);
  const [pendingExhibitions, setPendingExhibitions] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showArtworkPicker, setShowArtworkPicker] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingExhibition, setRejectingExhibition] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [editingExhibition, setEditingExhibition] = useState(null);
  const [activeTab, setActiveTab] = useState('all'); // all, pending
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const { token } = useAuth();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cover_image: '',
    organizer: '',
    location: '',
    start_date: null,
    end_date: null,
    is_online: true,
    is_featured: false,
    show_in_events: false,
    artwork_ids: []
  });

  useEffect(() => {
    fetchExhibitions();
    fetchPendingExhibitions();
    fetchArtworks();
  }, []);

  const fetchExhibitions = async () => {
    try {
      const res = await axios.get(`${API}/exhibitions/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExhibitions(res.data);
    } catch (err) {
      console.error('Failed to fetch exhibitions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingExhibitions = async () => {
    try {
      const res = await axios.get(`${API}/exhibitions/pending/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingExhibitions(res.data);
    } catch (err) {
      console.error('Failed to fetch pending exhibitions:', err);
    }
  };

  const handleApprove = async (exhibitionId) => {
    try {
      await axios.post(`${API}/exhibitions/${exhibitionId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('展览审核通过');
      fetchExhibitions();
      fetchPendingExhibitions();
    } catch (err) {
      toast.error('审核失败');
    }
  };

  const handleReject = async () => {
    if (!rejectingExhibition) return;
    try {
      await axios.post(`${API}/exhibitions/${rejectingExhibition.id}/reject?reason=${encodeURIComponent(rejectReason)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('展览已拒绝');
      setShowRejectDialog(false);
      setRejectingExhibition(null);
      setRejectReason('');
      fetchExhibitions();
      fetchPendingExhibitions();
    } catch (err) {
      toast.error('操作失败');
    }
  };

  const fetchArtworks = async () => {
    try {
      const res = await axios.get(`${API}/artworks?limit=100`);
      setArtworks(res.data);
    } catch (err) {
      console.error('Failed to fetch artworks:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      cover_image: '',
      organizer: '',
      location: '',
      start_date: null,
      end_date: null,
      is_online: true,
      is_featured: false,
      show_in_events: false,
      artwork_ids: []
    });
    setEditingExhibition(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (exhibition) => {
    setEditingExhibition(exhibition);
    setFormData({
      title: exhibition.title || '',
      description: exhibition.description || '',
      cover_image: exhibition.cover_image || '',
      organizer: exhibition.organizer || '',
      location: exhibition.location || '',
      start_date: exhibition.start_date ? new Date(exhibition.start_date) : null,
      end_date: exhibition.end_date ? new Date(exhibition.end_date) : null,
      is_online: exhibition.is_online ?? true,
      is_featured: exhibition.is_featured ?? false,
      show_in_events: exhibition.show_in_events ?? false,
      artwork_ids: exhibition.artwork_ids || []
    });
    setShowDialog(true);
  };

  const handleUploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
      });
      setFormData({ ...formData, cover_image: res.data.url });
      toast.success('封面上传成功');
    } catch (err) {
      toast.error('上传失败');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('请输入画展标题');
      return;
    }

    try {
      const data = {
        ...formData,
        start_date: formData.start_date?.toISOString(),
        end_date: formData.end_date?.toISOString()
      };

      if (editingExhibition) {
        await axios.put(`${API}/exhibitions/${editingExhibition.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('画展更新成功');
      } else {
        await axios.post(`${API}/exhibitions`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('画展创建成功');
      }
      
      setShowDialog(false);
      resetForm();
      fetchExhibitions();
    } catch (err) {
      toast.error(err.response?.data?.detail || '保存失败');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('确定要删除这个画展吗？')) return;

    try {
      await axios.delete(`${API}/exhibitions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('画展已删除');
      fetchExhibitions();
    } catch (err) {
      toast.error('删除失败');
    }
  };

  const handlePublish = async (id) => {
    try {
      await axios.post(`${API}/exhibitions/${id}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('画展已发布');
      fetchExhibitions();
    } catch (err) {
      toast.error('发布失败');
    }
  };

  const toggleArtwork = (artworkId) => {
    const current = formData.artwork_ids || [];
    if (current.includes(artworkId)) {
      setFormData({ ...formData, artwork_ids: current.filter(id => id !== artworkId) });
    } else {
      setFormData({ ...formData, artwork_ids: [...current, artworkId] });
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-amber-100 text-amber-700',
      published: 'bg-green-100 text-green-700',
      ended: 'bg-red-100 text-red-700'
    };
    const labels = {
      draft: '草稿',
      published: '已发布',
      ended: '已结束'
    };
    return <Badge className={styles[status] || styles.draft}>{labels[status] || '草稿'}</Badge>;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">画展管理</h2>
          <p className="text-amber-600">创建和管理画展专题</p>
        </div>
        <Button onClick={handleCreate} data-testid="create-exhibition-btn">
          <Plus className="w-4 h-4 mr-2" /> 创建画展
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin mx-auto"></div>
        </div>
      ) : exhibitions.length === 0 ? (
        <Card className="text-center py-12">
          <ImageIcon className="w-16 h-16 mx-auto text-amber-400 mb-4" />
          <p className="text-amber-600">暂无画展</p>
          <Button variant="outline" className="mt-4" onClick={handleCreate}>
            创建第一个画展
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4">
          {exhibitions.map((exhibition) => (
            <Card key={exhibition.id} className="overflow-hidden">
              <div className="flex">
                {/* 封面图 */}
                <div className="w-48 h-32 bg-amber-100 flex-shrink-0">
                  {exhibition.cover_image ? (
                    <img
                      src={getImageUrl(exhibition.cover_image)}
                      alt={exhibition.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-amber-400" />
                    </div>
                  )}
                </div>
                
                {/* 信息 */}
                <div className="flex-1 p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="text-lg font-medium">{exhibition.title}</h3>
                        {getStatusBadge(exhibition.status)}
                        {exhibition.is_featured && (
                          <Badge className="bg-amber-100 text-amber-700">精选</Badge>
                        )}
                        {exhibition.show_in_events && (
                          <Badge className="bg-green-100 text-green-700">精彩活动</Badge>
                        )}
                      </div>
                      <p className="text-sm text-amber-600 line-clamp-1 mb-2">
                        {exhibition.description || '暂无简介'}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-amber-500">
                        <span>作品: {exhibition.artwork_count || 0}</span>
                        <span>浏览: {exhibition.view_count || 0}</span>
                        {exhibition.start_date && (
                          <span>
                            {format(new Date(exhibition.start_date), 'yyyy/MM/dd')}
                            {exhibition.end_date && ` - ${format(new Date(exhibition.end_date), 'yyyy/MM/dd')}`}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => window.open(`/exhibitions/${exhibition.id}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(exhibition)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {exhibition.status === 'draft' && (
                        <Button variant="ghost" size="icon" onClick={() => handlePublish(exhibition.id)}>
                          <Check className="w-4 h-4 text-green-600" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(exhibition.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* 创建/编辑对话框 */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingExhibition ? '编辑画展' : '创建画展'}</DialogTitle>
            <DialogDescription>填写画展信息并选择要收录的作品</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* 提示信息 */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              💡 填写内容后，其他语言将自动翻译显示
            </div>

            {/* 画展标题 - 单一输入框 */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span className="text-red-500">*</span>
                <span>画展标题 / Exhibition Title</span>
              </Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="输入画展标题 / Enter exhibition title"
              />
            </div>

            {/* 画展简介 - 单一输入框 */}
            <div className="space-y-2">
              <Label>画展简介 / Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="描述这个画展... / Describe this exhibition..."
                rows={3}
              />
            </div>
            {/* 封面图 */}
            <div className="space-y-2">
              <Label>封面图片 / Cover Image</Label>
              <div className="flex gap-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleUploadCover}
                  className="hidden"
                />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? '上传中... / Uploading...' : '上传本地图片 / Upload Image'}
                </Button>
                <span className="text-sm text-gray-500 self-center">或 / or</span>
                <Input
                  value={formData.cover_image}
                  onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
                  placeholder="图片URL / Image URL"
                  className="flex-1"
                />
              </div>
              {formData.cover_image && (
                <img src={getImageUrl(formData.cover_image)} alt="封面预览" className="w-48 h-32 object-cover rounded mt-2" />
              )}
            </div>

            {/* 主办方 - 单一输入框 */}
            <div className="space-y-2">
              <Label>主办方 / Organizer</Label>
              <Input
                value={formData.organizer}
                onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
                placeholder="主办单位名称 / Organizer name"
              />
            </div>
            
            {/* 展览地点 - 单一输入框 */}
            <div className="space-y-2">
              <Label>展览地点 / Location</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="例如：北京798艺术区 / e.g. Beijing 798 Art Zone"
              />
            </div>

            {/* 日期 */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>开始日期</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formData.start_date ? format(formData.start_date, 'yyyy年M月d日', { locale: zhCN }) : '选择日期'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.start_date}
                      onSelect={(date) => setFormData({ ...formData, start_date: date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>结束日期</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <CalendarIcon className="w-4 h-4 mr-2" />
                      {formData.end_date ? format(formData.end_date, 'yyyy年M月d日', { locale: zhCN }) : '选择日期'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={formData.end_date}
                      onSelect={(date) => setFormData({ ...formData, end_date: date })}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* 开关选项 */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_online}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_online: checked })}
                />
                <Label>线上展览</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_featured: checked })}
                />
                <Label>精选推荐</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.show_in_events}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_in_events: checked })}
                />
                <Label className="text-amber-700 font-medium">显示在精彩活动</Label>
              </div>
            </div>

            {/* 选择作品 */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>收录作品 ({formData.artwork_ids?.length || 0}件)</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setShowArtworkPicker(true)}>
                  <Plus className="w-4 h-4 mr-1" /> 选择作品
                </Button>
              </div>
              {formData.artwork_ids?.length > 0 && (
                <div className="flex flex-wrap gap-2 p-3 bg-amber-50 rounded-lg">
                  {formData.artwork_ids.map((id) => {
                    const artwork = artworks.find(a => a.id === id);
                    return artwork ? (
                      <div key={id} className="relative group">
                        <img
                          src={getImageUrl(artwork.image_url)}
                          alt={artwork.title}
                          className="w-16 h-16 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => toggleArtwork(id)}
                          className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>取消</Button>
            <Button onClick={handleSave}>{editingExhibition ? '保存' : '创建'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 作品选择器 */}
      <Dialog open={showArtworkPicker} onOpenChange={setShowArtworkPicker}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>选择作品</DialogTitle>
            <DialogDescription>点击作品添加或移除</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-4 gap-3">
            {artworks.map((artwork) => {
              const isSelected = formData.artwork_ids?.includes(artwork.id);
              return (
                <div
                  key={artwork.id}
                  onClick={() => toggleArtwork(artwork.id)}
                  className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                    isSelected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent hover:border-amber-300'
                  }`}
                >
                  <img
                    src={getImageUrl(artwork.image_url)}
                    alt={artwork.title}
                    className="w-full aspect-square object-cover"
                  />
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4" />
                    </div>
                  )}
                  <div className="p-2">
                    <p className="text-sm font-medium line-clamp-1">{artwork.title}</p>
                    <p className="text-xs text-amber-600">{artwork.creator_name}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowArtworkPicker(false)}>完成</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminExhibitions;
